import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@frontend-platform/ui-lib/ui';
import { Plus, Trash2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { type BotProperty, readBotProperties, writeBotProperties } from './dev-bot-properties';
import { pushDevSession } from './dev-session';

export interface DevShellBotPropertiesDialogProps {
  onClose: () => void;
}

/** Mount it to open it; it reads the stored properties once on mount. */
export function DevShellBotPropertiesDialog({ onClose }: DevShellBotPropertiesDialogProps) {
  const [rows, setRows] = useState<BotProperty[]>(readBotProperties);
  const [saveError, setSaveError] = useState(false);

  function update(index: number, field: keyof BotProperty, value: string) {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!writeBotProperties(rows)) {
      setSaveError(true);
      return;
    }
    await pushDevSession();
    window.location.reload();
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-140 max-w-[calc(100vw-2rem)] sm:max-w-140">
        <form className="grid gap-5" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>AI Agent properties</DialogTitle>
            <DialogDescription>
              Edits currentBot.properties in localStorage, the bot flags apps read in standalone
              dev. Saved values apply after the reload.
            </DialogDescription>
          </DialogHeader>

          <div className="grid max-h-80 gap-2 overflow-y-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-steel-light">No properties yet. Add one below.</p>
            ) : null}
            {rows.map((row, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input
                  aria-label={`Property ${index + 1} name`}
                  value={row.key}
                  onChange={(event) => update(index, 'key', event.target.value)}
                  className="font-mono text-xs"
                  autoComplete="off"
                />
                <Input
                  aria-label={`Property ${index + 1} value`}
                  value={row.value}
                  onChange={(event) => update(index, 'value', event.target.value)}
                  className="w-40 shrink-0 font-mono text-xs"
                  autoComplete="off"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove ${row.key || `property ${index + 1}`}`}
                  onClick={() => setRows((current) => current.filter((_, i) => i !== index))}
                  className="size-8 shrink-0 p-0 text-steel-light"
                >
                  <Trash2 size={14} aria-hidden />
                </Button>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRows((current) => [...current, { key: '', value: 'true' }])}
            className="justify-self-start"
          >
            <Plus size={14} aria-hidden />
            Add property
          </Button>

          {saveError ? (
            <p className="text-destructive text-sm">
              Could not save the properties. Check browser storage permissions.
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save and reload</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
